import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { CommandService } from '@helios/commands'
import { ClearwaterTransaction, ExternalTransaction } from '@helios/common-api'
import { GenericTxSummary } from '@helios/generic-components'
import {
  HeliosServerSideRequest,
  genericHeliosRequest,
} from '@helios/server-side-util'
import {
  HANDLE_ERROR_AND_THROW,
  HELIOS_COMMAND_HTTP_TOKEN,
} from '@helios/utils'
import { Observable, of } from 'rxjs'
import { map, take } from 'rxjs/operators'
import {
  ExportExcelRequest,
  ExternalTransactionSetValues,
  TransactionAvailableRestrictedColumns,
} from '../models/external-transaction'
import {
  ExternalActionType,
  ExternalTransactionsActionRequest,
  ExternalTransactionsActionResponse,
  createExternalTransactionsActionRequest,
} from '../models/external-transactions-action.model'
import { ExternalTransactionsService } from './external-transactions.service'

/**
 * @internal
 */
export const TRANSACTIONS_URL = 'helios-ext-transactions'
export const EXPORT_URL = `${TRANSACTIONS_URL}/export`
export const TRANSACTIONS_SET_VALUES_URL = `${TRANSACTIONS_URL}/setValues`
export const TRANSACTIONS_ACTIONS_URL = `${TRANSACTIONS_URL}/actions`
export const TRANSACTIONS_SUMMARIES_URL = `${TRANSACTIONS_URL}/summaries`
export const TRANSACTIONS_RESTRICTED_COLUMNS_URL = `${TRANSACTIONS_URL}/transactionRestrictedColumns`

const PANEL_NAME = 'External Transactions'

/**
 * @internal
 */
export interface GetTransactionsResponse {
  data?: ExternalTransaction[]
}

@Injectable({
  providedIn: 'root',
})
export class HttpExternalTransactionsService extends ExternalTransactionsService {
  constructor(
    private readonly http: HttpClient,
    private readonly commandService: CommandService,
  ) {
    super()
  }

  async getTransactions(
    request: HeliosServerSideRequest<ExternalTransaction>,
    cacheLoaded$: Observable<boolean> = of(true),
  ): Promise<ExternalTransaction[]> {
    const response = await this.http
      .post<GetTransactionsResponse>(TRANSACTIONS_URL, request)
      .pipe(take(1))
      .toPromise()

    const extTxs = response.data ?? []

    const cacheLoaded = await cacheLoaded$.pipe(take(1)).toPromise()

    if (cacheLoaded) {
      return extTxs
    }

    const totalRows = request.startRow + extTxs.length
    if (totalRows < request.endRow) {
      await new Promise(resolve => setTimeout(() => resolve(null), 500))
      return this.getTransactions(request, cacheLoaded$)
    }

    return extTxs
  }

  getTransactionsByIds(
    extTxIds: string[],
    fields?: string[],
  ): Promise<ExternalTransaction[]> {
    return this.getTransactions(
      this.getHeliosRequestFromPositionIds(extTxIds, fields),
    )
  }

  private getHeliosRequestFromPositionIds(
    extTxIds: string[],
    fields?: string[],
  ) {
    //The backend handles the row range like this [startRow, endRow) ie include start row up until but not including endRow
    return {
      ...genericHeliosRequest,
      endRow: extTxIds.length,
      fields,
      filter: {
        extTranId: {
          filterType: 'number',
          operator: 'AND',
          filters: [
            {
              operator: 'in',
              values: [extTxIds.join(', ')],
            },
          ],
        },
      },
    } as HeliosServerSideRequest<ExternalTransaction>
  }

  getExternalTransactionSetValues(): Observable<ExternalTransactionSetValues> {
    return this.http.get<ExternalTransactionSetValues>(
      TRANSACTIONS_SET_VALUES_URL,
    )
  }

  hold(
    extTrans: ExternalTransaction[],
  ): Observable<ExternalTransactionsActionResponse> {
    const request = createExternalTransactionsActionRequest(
      ExternalActionType.hold,
      extTrans.map(tx => `${tx.rowId}`),
    )
    return this.callActionsApi(request)
  }

  unhold(
    extTrans: ExternalTransaction[],
  ): Observable<ExternalTransactionsActionResponse> {
    const request = createExternalTransactionsActionRequest(
      ExternalActionType.unhold,
      extTrans.map(tx => `${tx.rowId}`),
    )
    return this.callActionsApi(request)
  }

  ignore(
    extTrans: ExternalTransaction[],
  ): Observable<ExternalTransactionsActionResponse> {
    const request = createExternalTransactionsActionRequest(
      ExternalActionType.ignore,
      extTrans.map(tx => `${tx.rowId}`),
    )
    return this.callActionsApi(request)
  }

  unignore(
    extTrans: ExternalTransaction[],
  ): Observable<ExternalTransactionsActionResponse> {
    const request = createExternalTransactionsActionRequest(
      ExternalActionType.unignore,
      extTrans.map(tx => `${tx.rowId}`),
    )
    return this.callActionsApi(request)
  }

  match(
    extTrans: ExternalTransaction[],
    cwTrans: ClearwaterTransaction[],
  ): Observable<ExternalTransactionsActionResponse> {
    const request = createExternalTransactionsActionRequest(
      ExternalActionType.match,
      extTrans.map(tx => `${tx.rowId}`),
      cwTrans.map(tx => tx.rowId),
    )
    return this.callActionsApi(request)
  }

  manyToManyMatch(
    extTrans: ExternalTransaction[],
    cwTrans: ClearwaterTransaction[],
  ): Observable<ExternalTransactionsActionResponse> {
    const request = createExternalTransactionsActionRequest(
      ExternalActionType.manyToManyMatch,
      extTrans.map(tx => `${tx.rowId}`),
      cwTrans.map(tx => tx.rowId),
    )
    return this.callActionsApi(request)
  }

  matchUsingExternal(
    extTran: ExternalTransaction,
    cwTrans: ClearwaterTransaction[],
  ): Observable<ExternalTransactionsActionResponse> {
    const request = createExternalTransactionsActionRequest(
      ExternalActionType.matchUsingExternal,
      [`${extTran.rowId}`],
      cwTrans.map(tx => tx.rowId),
    )

    return this.callActionsApi(request)
  }

  unmatch(
    extTrans: ExternalTransaction[],
  ): Observable<ExternalTransactionsActionResponse> {
    const request = createExternalTransactionsActionRequest(
      ExternalActionType.unmatch,
      extTrans.map(tx => `${tx.rowId}`),
    )
    return this.callActionsApi(request)
  }

  private callActionsApi(
    request: ExternalTransactionsActionRequest,
  ): Observable<ExternalTransactionsActionResponse> {
    return this.http.post<ExternalTransactionsActionResponse>(
      TRANSACTIONS_ACTIONS_URL,
      request,
      {
        headers: this.commandService.generateTemporaryHeaders(
          PANEL_NAME,
          request.actionType,
        ),
        context: new HttpContext().set(HELIOS_COMMAND_HTTP_TOKEN, true),
      },
    )
  }

  exportToExcel(request: ExportExcelRequest): Observable<Blob> {
    const options = {
      responseType: 'blob' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    const requestPayload = {
      ...genericHeliosRequest,
      headers: request.headers,
      fields: request.fields,
      filter: request.filter,
      userTimeZone: request.userTimeZone,
    }

    return this.http.post(EXPORT_URL, requestPayload, options)
  }

  async getAllExtTransactionsIds(
    request: HeliosServerSideRequest<ExternalTransaction>,
  ): Promise<string[]> {
    const rowIds = await this.http
      .post<GetTransactionsResponse>(TRANSACTIONS_URL, {
        ...request,
        startRow: 0,
        endRow: 100001,
        fields: ['rowId'],
      })
      .pipe(
        take(1),
        map(response => response.data?.map(tx => tx.rowId) as string[]),
      )
      .toPromise()

    return rowIds
  }

  async getTransactionSummaries(
    request: HeliosServerSideRequest<ExternalTransaction>,
  ): Promise<GenericTxSummary> {
    const { data } = await this.http
      .post<{
        data: GenericTxSummary[]
      }>(TRANSACTIONS_SUMMARIES_URL, request)
      .toPromise()
    return data[0]
  }

  getAvailableRestrictedColumns(): Observable<
    TransactionAvailableRestrictedColumns[]
  > {
    return this.http.get<TransactionAvailableRestrictedColumns[]>(
      TRANSACTIONS_RESTRICTED_COLUMNS_URL,
    )
  }
}
